/**
 * Expanded admin panel for one registrar domain.
 * Renders the full raw WHOIS payload from TPP (registrant/admin/tech/billing
 * contacts, registration date, autorenew, status, lock), live nameserver +
 * DNS zone management, renewal cost history, and order log.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  History, Loader2, Lock, LockOpen, RefreshCw, Repeat, Save, ShieldCheck, Timer,
} from "lucide-react";
import { ZoneRecordsEditor } from "@/components/domains/ZoneRecordsEditor";

export function DomainDetailPanel({ domain, onDomainUpdated }) {
  const [d, setD] = useState(domain);
  const isRest = d.account_auth_type === "rest";
  const [refreshing, setRefreshing] = useState(false);
  const [nsDraft, setNsDraft] = useState((domain.nameservers || []).join("\n"));
  const [nsBusy, setNsBusy] = useState(false);
  const [renewPeriod, setRenewPeriod] = useState("1");
  const [renewBusy, setRenewBusy] = useState(false);
  const [orders, setOrders] = useState(null);
  const [bus, setBus] = useState([]);
  const [propBu, setPropBu] = useState(domain.business_unit_id || "");
  const [propPrimary, setPropPrimary] = useState(!!domain.is_primary);
  const [cost, setCost] = useState(domain.renewal_cost ?? "");
  const [costFreq, setCostFreq] = useState(
    domain.renewal_cost_frequency || "annual");
  const [propBusy, setPropBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);

  useEffect(() => {
    if (!d.client_id) { setBus([]); return; }
    api.get(`/clients/${d.client_id}/business-units`)
      .then((r) => setBus(r.data || []))
      .catch(() => setBus([]));
  }, [d.client_id]);

  useEffect(() => {
    api.get(`/domains/${domain.domain_id}/orders`)
      .then((r) => setOrders(r.data?.orders || []))
      .catch(() => setOrders([]));
  }, [domain.domain_id]);

  // Details (WHOIS + contacts) and DNS zone records are cached in the local
  // DB — we only fetch fresh data from TPP when the user explicitly clicks
  // "Refresh from TPP". This keeps the UI instant and TPP API usage low.

  const saveProperty = async () => {
    setPropBusy(true);
    try {
      const { data } = await api.patch(`/domains/${d.domain_id}/property`, {
        business_unit_id: propBu,
        is_primary: propPrimary,
        renewal_cost: cost === "" ? null : Number(cost),
        clear_renewal_cost: cost === "",
        renewal_cost_frequency: costFreq,
      });
      setD({ ...d, ...data });
      toast.success("Property assignment saved");
      onDomainUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setPropBusy(false); }
  };

  const refreshDetails = async () => {
    setRefreshing(true);
    try {
      // Fire WHOIS refresh + live zone-records pull in parallel — both write
      // through to the local DB so subsequent opens use the cache.
      const [detailsRes, zonesRes] = await Promise.all([
        api.post(`/domains/${d.domain_id}/refresh-details`),
        api.get(`/domains/${d.domain_id}/zone-records`).catch(() => ({ data: null })),
      ]);
      const data = detailsRes.data || {};
      const zones = zonesRes?.data?.records;
      setD((prev) => ({
        ...prev,
        ...data,
        ...(zones ? { zone_records: zones } : {}),
      }));
      setNsDraft((data.nameservers || []).join("\n"));
      toast.success("Details refreshed from TPP Wholesale");
      onDomainUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  const saveNameservers = async () => {
    const ns = nsDraft.split("\n").map((n) => n.trim()).filter(Boolean);
    if (!window.confirm(`Replace ALL nameservers for ${d.domain} with:\n\n${ns.join("\n")}\n\nThis submits a live change to TPP Wholesale.`)) return;
    setNsBusy(true);
    try {
      const { data } = await api.post(`/domains/${d.domain_id}/nameservers`, { nameservers: ns });
      toast.success(`Nameserver order ${data.order_id} submitted`);
      setOrders((o) => [data, ...(o || [])]);
      onDomainUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nameserver update failed");
    } finally { setNsBusy(false); }
  };

  const renew = async () => {
    if (!window.confirm(`Renew ${d.domain} for ${renewPeriod} year(s)? This places a REAL renewal order billed to your TPP prepaid account.`)) return;
    setRenewBusy(true);
    try {
      const { data } = await api.post(`/domains/${d.domain_id}/renew`, { period: Number(renewPeriod) });
      toast.success(`Renewal order ${data.order_id} submitted`);
      setOrders((o) => [data, ...(o || [])]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Renewal failed");
    } finally { setRenewBusy(false); }
  };

  const checkOrders = async () => {
    try {
      const { data } = await api.get(`/domains/${d.domain_id}/orders`, { params: { check: true } });
      setOrders(data?.orders || []);
      toast.success("Order statuses refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status check failed");
    }
  };

  const toggleAutorenew = async () => {
    const next = !d.autorenew;
    setAutoBusy(true);
    try {
      await api.post(`/domains/${d.domain_id}/autorenew`, { autorenew: next });
      setD({ ...d, autorenew: next });
      toast.success(`Auto-renew ${next ? "enabled" : "disabled"}`);
      onDomainUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setAutoBusy(false); }
  };

  const toggleLock = async () => {
    const isLocked = d.lock_status === "2";
    const action = isLocked ? "unlock" : "lock";
    if (!window.confirm(`${action === "lock" ? "Lock" : "Unlock"} ${d.domain} at the registrar?`)) return;
    setLockBusy(true);
    try {
      await api.post(`/domains/${d.domain_id}/lock`, null, { params: { action } });
      setD({ ...d, lock_status: action === "lock" ? "2" : "1" });
      toast.success(`Domain ${action}ed`);
      onDomainUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    } finally { setLockBusy(false); }
  };

  return (
    <div className="bg-zinc-50/60 border-t border-zinc-200 p-4 space-y-4"
      data-testid={`domain-detail-${d.domain_id}`}>
      {/* Header: registrar summary + refresh */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-overline flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3" /> Registrar details
            </div>
            <div className="text-[10px] font-mono text-zinc-400 mt-0.5">
              {d.details_fetched_at
                ? <>WHOIS cached {new Date(d.details_fetched_at).toLocaleString("en-AU", {dateStyle: "short", timeStyle: "short"})}</>
                : <>Not yet fetched — click <strong>Refresh from TPP</strong> to load details</>}
            </div>
          </div>
          <Button size="sm" variant="outline" className="rounded-sm h-7"
            onClick={refreshDetails} disabled={refreshing}
            data-testid={`domain-refresh-${d.domain_id}`}>
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Refresh from TPP
          </Button>
        </div>

        <RegistrarSummary d={d} isRest={isRest}
          onToggleAutorenew={toggleAutorenew} autoBusy={autoBusy}
          onToggleLock={toggleLock} lockBusy={lockBusy} />
      </div>

      {/* Nameservers — full width so the input has room */}
      <div className="border-t border-zinc-200 pt-3">
        <div className="label-overline mb-1 flex items-center gap-1.5">
          Nameservers (one per line)
          {refreshing && (d.nameservers || []).length === 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono text-zinc-400 normal-case tracking-normal">
              <Loader2 className="h-3 w-3 animate-spin" /> loading from TPP…
            </span>
          )}
        </div>
        <textarea value={nsDraft} onChange={(e) => setNsDraft(e.target.value)}
          rows={4}
          className="w-full rounded-sm border border-zinc-200 text-xs font-mono p-2 bg-white"
          placeholder={refreshing
            ? "Fetching current nameservers from TPP…"
            : "ns1.example.com\nns2.example.com"}
          data-testid={`domain-ns-input-${d.domain_id}`} />
        <Button size="sm" onClick={saveNameservers} disabled={nsBusy || refreshing}
          className="rounded-sm h-7 mt-1 bg-zinc-900 hover:bg-black text-white"
          data-testid={`domain-ns-save-${d.domain_id}`}>
          {nsBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
          Update nameservers at registrar
        </Button>
      </div>

      {/* Zone Records — full width so the TTL field, host + value all fit */}
      <div className="border-t border-zinc-200 pt-3">
        <ZoneRecordsEditor domain={d} isLive={isRest} onChanged={async () => {
          const { data } = await api.get(`/domains`, { params: { search: d.domain, show_expired: true } });
          const fresh = (data?.domains || []).find((x) => x.domain_id === d.domain_id);
          if (fresh) setD({ ...d, ...fresh });
        }} />
      </div>

      {/* WHOIS contacts */}
      {isRest && d.contacts && (
        <div className="border-t border-zinc-200 pt-3">
          <ContactsBlock contacts={d.contacts} />
        </div>
      )}

      {/* Renewal cost history */}
      {d.renewal_cost_history && d.renewal_cost_history.length > 0 && (
        <div className="border-t border-zinc-200 pt-3">
          <RenewalHistory history={d.renewal_cost_history} currency={d.renewal_currency || "AUD"} />
        </div>
      )}

      {isRest && d.raw_whois && (
        <div className="border-t border-zinc-200 pt-3">
          <RawWhoisBlock raw={d.raw_whois} />
        </div>
      )}

      {/* Property assignment */}
      <div className="border-t border-zinc-200 pt-3">
        <PropertyBlock d={d} bus={bus} propBu={propBu} setPropBu={setPropBu}
          propPrimary={propPrimary} setPropPrimary={setPropPrimary}
          cost={cost} setCost={setCost}
          costFreq={costFreq} setCostFreq={setCostFreq}
          propBusy={propBusy} onSave={saveProperty} />
      </div>

      {/* Legacy manual renew (only for non-REST accounts) */}
      {!isRest && (
        <div className="border-t border-zinc-200 pt-3">
          <div className="label-overline mb-1 flex items-center gap-1.5">
            <Timer className="h-3 w-3" /> Renew domain
          </div>
          <div className="flex items-center gap-1.5">
            <select value={renewPeriod} onChange={(e) => setRenewPeriod(e.target.value)}
              className="h-8 rounded-sm border border-zinc-200 text-xs font-mono px-1 bg-white"
              data-testid={`domain-renew-period-${d.domain_id}`}>
              {[1, 2, 3, 5, 10].map((y) => <option key={y} value={y}>{y} year{y > 1 ? "s" : ""}</option>)}
            </select>
            <Button size="sm" onClick={renew} disabled={renewBusy}
              className="rounded-sm h-8 bg-[#002FA7] hover:bg-[#001f70] text-white"
              data-testid={`domain-renew-${d.domain_id}`}>
              {renewBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Renew now
            </Button>
          </div>
        </div>
      )}

      {orders !== null && orders.length > 0 && (
        <div className="border-t border-zinc-200 pt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="label-overline">Recent orders</span>
            <button type="button" onClick={checkOrders}
              className="text-[10px] font-mono text-[#002FA7] hover:underline"
              data-testid={`domain-orders-check-${d.domain_id}`}>
              Check statuses
            </button>
          </div>
          {orders.slice(0, 5).map((o) => (
            <div key={o.order_id} className="text-[10px] font-mono text-zinc-600 flex items-center gap-1.5 flex-wrap">
              <span className="px-1 rounded-sm bg-zinc-100">{o.order_id}</span>
              {o.action} · {o.detail}
              <span className={`px-1 rounded-sm ${
                o.status === "complete" ? "bg-emerald-50 text-emerald-700"
                  : o.status === "failed" ? "bg-red-50 text-red-700"
                    : "bg-amber-50 text-amber-700"}`}>
                {o.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RegistrarSummary({ d, isRest, onToggleAutorenew, autoBusy, onToggleLock, lockBusy }) {
  const isLocked = d.lock_status === "2";
  return (
    <div className="grid grid-cols-2 gap-2 text-[11px]">
      <div><span className="text-zinc-500">Expiry:</span> <span className="font-mono">{d.expiry_date || "—"}</span></div>
      <div><span className="text-zinc-500">Registered:</span> <span className="font-mono">{d.registration_date || "—"}</span></div>
      <div><span className="text-zinc-500">Status:</span> <span className="font-mono">{d.domain_status || d.list_status || "—"}</span></div>
      <div><span className="text-zinc-500">Category:</span> <span className="font-mono">{d.category || "—"}</span></div>
      <div><span className="text-zinc-500">Account ref:</span> <span className="font-mono">{d.account_ref || "—"}</span></div>
      <div><span className="text-zinc-500">Live cost /yr:</span> <span className="font-mono">
        {d.renewal_cost_api != null ? `$${Number(d.renewal_cost_api).toFixed(2)}` : "—"}
      </span></div>
      <div className="col-span-2 flex items-center gap-1.5 flex-wrap pt-1">
        {isRest ? (
          <>
            <Button size="sm" variant="outline" onClick={onToggleAutorenew} disabled={autoBusy}
              className="rounded-sm h-7 text-[11px]"
              data-testid={`domain-autorenew-toggle-${d.domain_id}`}>
              {autoBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Repeat className="h-3 w-3 mr-1" />}
              Auto-renew: <strong className="ml-1">{d.autorenew ? "ON" : "OFF"}</strong>
            </Button>
            <Button size="sm" variant="outline" onClick={onToggleLock} disabled={lockBusy}
              className="rounded-sm h-7 text-[11px]"
              data-testid={`domain-lock-toggle-${d.domain_id}`}>
              {lockBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                : isLocked ? <Lock className="h-3 w-3 mr-1" /> : <LockOpen className="h-3 w-3 mr-1" />}
              {isLocked ? "Locked" : "Unlocked"}
            </Button>
          </>
        ) : (
          <span className="text-[10px] font-mono text-zinc-500">
            Lock: {isLocked ? "Locked" : d.lock_status === "1" ? "Unlocked" : "—"}
          </span>
        )}
      </div>
    </div>
  );
}

function ContactsBlock({ contacts }) {
  const roles = [
    { key: "registrant", label: "Registrant" },
    { key: "admin", label: "Admin" },
    { key: "tech", label: "Tech" },
    { key: "billing", label: "Billing" },
  ];
  const present = roles.filter((r) => contacts[r.key] && Object.keys(contacts[r.key]).length > 0);
  if (present.length === 0) return null;
  return (
    <div className="border-t border-zinc-200 pt-2 space-y-1.5"
      data-testid="domain-contacts-block">
      <div className="label-overline">WHOIS contacts</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {present.map((r) => {
          const c = contacts[r.key] || {};
          const name = [c.firstName, c.lastName].filter(Boolean).join(" ");
          return (
            <div key={r.key} className="border border-zinc-200 rounded-sm bg-white p-2 text-[11px]"
              data-testid={`domain-contact-${r.key}`}>
              <div className="font-mono text-[9px] uppercase text-zinc-500 mb-1">{r.label}</div>
              {name && <div className="font-medium">{name}</div>}
              {c.org && <div className="text-zinc-600">{c.org}</div>}
              {c.email && <div className="text-zinc-600 font-mono text-[10px]">{c.email}</div>}
              {c.phone && <div className="text-zinc-500 font-mono text-[10px]">{c.phone}</div>}
              {(c.address1 || c.city || c.state) && (
                <div className="text-zinc-500 text-[10px] mt-0.5">
                  {[c.address1, c.address2, c.address3, c.city, c.state, c.postalCode, c.country]
                    .filter(Boolean).join(", ")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RenewalHistory({ history, currency }) {
  const last = history[history.length - 1];
  return (
    <div className="border-t border-zinc-200 pt-2 space-y-1"
      data-testid="domain-renewal-history">
      <div className="label-overline flex items-center gap-1.5">
        <History className="h-3 w-3" /> Renewal cost history
        <span className="text-zinc-400 normal-case">({currency})</span>
      </div>
      <div className="text-[10px] text-zinc-500">
        Latest: <strong className="font-mono">${Number(last.cost).toFixed(2)}</strong> on {last.date}
      </div>
      <div className="max-h-32 overflow-y-auto border border-zinc-200 rounded-sm bg-white">
        <table className="w-full text-[10px]">
          <thead className="bg-zinc-50 text-zinc-500 font-mono uppercase text-[9px]">
            <tr><th className="text-left px-2 py-1">Date</th><th className="text-right px-2 py-1">Cost</th></tr>
          </thead>
          <tbody>
            {[...history].reverse().map((h, i) => (
              <tr key={`${h.date}-${i}`} className="border-t border-zinc-100">
                <td className="px-2 py-1 font-mono">{h.date}</td>
                <td className="px-2 py-1 font-mono text-right">${Number(h.cost).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RawWhoisBlock({ raw }) {
  const displayableKeys = Object.keys(raw || {}).filter((k) =>
    typeof raw[k] === "string" || typeof raw[k] === "number" || typeof raw[k] === "boolean"
  );
  if (displayableKeys.length === 0) return null;
  return (
    <div className="border-t border-zinc-200 pt-2 space-y-1"
      data-testid="domain-raw-whois">
      <div className="label-overline">All registry fields ({displayableKeys.length})</div>
      <div className="border border-zinc-200 rounded-sm bg-white">
        <table className="w-full text-[10px]">
          <tbody>
            {displayableKeys.map((k) => (
              <tr key={k} className="border-t border-zinc-100 first:border-t-0">
                <td className="px-2 py-1 font-mono text-zinc-500 w-1/3">{k}</td>
                <td className="px-2 py-1 font-mono break-all">{String(raw[k])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PropertyBlock({ d, bus, propBu, setPropBu, propPrimary, setPropPrimary,
                        cost, setCost, costFreq, setCostFreq,
                        propBusy, onSave }) {
  return (
    <div className="border-t border-zinc-200 pt-2 space-y-1.5"
      data-testid={`domain-property-block-${d.domain_id}`}>
      <div className="label-overline">Property assignment & cost</div>
      {!d.client_id ? (
        <div className="text-[11px] text-zinc-400 italic">
          Assign this domain to a client first, then pick the property it belongs to.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1.5 flex-wrap">
            <select value={propBu} onChange={(e) => setPropBu(e.target.value)}
              className="h-8 rounded-sm border border-zinc-200 text-xs px-1 bg-white min-w-[180px]"
              data-testid={`domain-property-select-${d.domain_id}`}>
              <option value="">— No property —</option>
              {bus.map((b) => (
                <option key={b.business_unit_id} value={b.business_unit_id}>{b.name}</option>
              ))}
            </select>
            <label className="inline-flex items-center gap-1.5 text-[11px] text-zinc-600">
              <input type="checkbox" checked={propPrimary}
                onChange={(e) => setPropPrimary(e.target.checked)}
                disabled={!propBu}
                className="accent-[#002FA7]"
                data-testid={`domain-primary-checkbox-${d.domain_id}`} />
              Primary domain {!propPrimary && propBu && <span className="text-zinc-400">(others show as redirected)</span>}
            </label>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-zinc-500">Client charge</span>
            <div className="flex items-center">
              <span className="h-8 px-1.5 grid place-items-center rounded-l-sm border border-r-0 border-zinc-200 bg-zinc-50 text-[11px] text-zinc-500">$</span>
              <Input type="number" step="0.01" min="0" value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="e.g. 29.95"
                className="h-8 w-24 rounded-none text-xs font-mono"
                data-testid={`domain-cost-${d.domain_id}`} />
              <select value={costFreq}
                onChange={(e) => setCostFreq(e.target.value)}
                className="h-8 rounded-l-none rounded-r-sm border border-l-0 border-zinc-200 bg-white text-[11px] text-zinc-700 px-1.5 focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                data-testid={`domain-cost-frequency-${d.domain_id}`}>
                <option value="monthly">/mo</option>
                <option value="quarterly">/qtr</option>
                <option value="annual">/yr</option>
              </select>
            </div>
            {d.renewal_cost_api != null && (
              <span className="text-[10px] text-zinc-400">TPP cost: ${Number(d.renewal_cost_api).toFixed(2)}</span>
            )}
            <Button size="sm" onClick={onSave} disabled={propBusy}
              className="rounded-sm h-8 bg-zinc-900 hover:bg-black text-white ml-auto"
              data-testid={`domain-property-save-${d.domain_id}`}>
              {propBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
